import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CurrencyConverter = () => {
  const [amount, setAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [result, setResult] = useState(null);

  const rates = {
    USD: { rate: 3.6725, flag: '🇺🇸', name: 'US Dollar' },
    GBP: { rate: 4.65, flag: '🇬🇧', name: 'British Pound' },
    EUR: { rate: 3.98, flag: '🇪🇺', name: 'Euro' },
    INR: { rate: 0.044, flag: '🇮🇳', name: 'Indian Rupee' },
    PKR: { rate: 0.013, flag: '🇵🇰', name: 'Pakistani Rupee' },
    SAR: { rate: 0.979, flag: '🇸🇦', name: 'Saudi Riyal' },
    CAD: { rate: 2.71, flag: '🇨🇦', name: 'Canadian Dollar' },
    AUD: { rate: 2.35, flag: '🇦🇺', name: 'Australian Dollar' },
  };

  const convert = () => {
    const val = parseFloat(amount?.replace(/,/g, ''));
    if (!val || isNaN(val)) return;
    const aed = val * rates?.[fromCurrency]?.rate;
    setResult({ from: val, fromCurrency, aed, rate: rates?.[fromCurrency]?.rate });
  };

  const hedgingStrategies = [
    { title: 'Forward Contracts', desc: 'Lock in today\'s exchange rate for future payments — ideal for off-plan installments', risk: 'Low' },
    { title: 'Multi-Currency Account', desc: 'Hold funds in AED-pegged accounts to eliminate conversion risk on UAE transactions', risk: 'Very Low' },
    { title: 'Staged Transfers', desc: 'Transfer funds in tranches to average out exchange rate fluctuations over time', risk: 'Medium' },
  ];

  return (
    <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <Icon name="RefreshCw" size={20} color="#00D4FF" />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#E8F4FF' }}>Currency Converter & Hedging</h3>
          <p className="text-sm" style={{ color: '#6B9EC4' }}>AED is pegged to USD at 3.6725 — stable for international buyers</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B9EC4' }}>Amount</label>
          <input
            type="text"
            value={amount}
            onChange={e => setAmount(e?.target?.value)}
            placeholder="Enter amount"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.2)', color: '#E8F4FF' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B9EC4' }}>From Currency</label>
          <select
            value={fromCurrency}
            onChange={e => setFromCurrency(e?.target?.value)}
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: 'rgba(10, 22, 40, 0.95)', border: '1px solid rgba(0, 212, 255, 0.2)', color: '#E8F4FF' }}
          >
            {Object.entries(rates)?.map(([code, info]) => (
              <option key={code} value={code}>{info?.flag} {code} — {info?.name}</option>
            ))}
          </select>
        </div>
      </div>
      <button
        onClick={convert}
        className="w-full py-3 rounded-xl font-semibold text-sm mb-4 transition-all duration-200"
        style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}
      >
        Convert to AED
      </button>
      {result && (
        <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <div className="text-center">
            <div className="text-sm" style={{ color: '#6B9EC4' }}>{result?.from?.toLocaleString()} {result?.fromCurrency} =</div>
            <div className="text-3xl font-bold my-1" style={{ color: '#00D4FF', fontFamily: "'JetBrains Mono', monospace" }}>AED {result?.aed?.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</div>
            <div className="text-xs" style={{ color: '#4A7A9B' }}>Rate: 1 {result?.fromCurrency} = AED {result?.rate}</div>
          </div>
        </div>
      )}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{ color: '#E8F4FF' }}>Currency Hedging Strategies</h4>
        <div className="space-y-3">
          {hedgingStrategies?.map((s, idx) => (
            <div key={idx} className="p-3 rounded-xl flex items-start gap-3" style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.12)' }}>
              <Icon name="Shield" size={16} color="#00D4FF" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: '#E8F4FF' }}>{s?.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0, 255, 136, 0.1)', color: '#00FF88', border: '1px solid rgba(0, 255, 136, 0.2)' }}>Risk: {s?.risk}</span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: '#6B9EC4' }}>{s?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CurrencyConverter;